import { CrudFilter, useInvalidate, useList } from '@refinedev/core';
import { FileData } from 'src/data-capture/hooks/file-uploads/types';

type MeetingNoteTense = 'THIRD_PERSON' | 'FIRST_SECOND_PERSON';

export type TemplateType =
  | 'SUITABILITY_REPORT'
  | 'MEETING_AGENDA'
  | 'MEETING_NOTE'
  | 'CLIENT_INFORMATION_DOCUMENT';

export type TemplateSection = {
  id: string;
  templateText: string;
  sectionHeader: string;
  sectionHeaderLevel: number;
  sectionInstructions: string;
  sectionPreview?: string;
  preserveAsIs: boolean;
  isTemplateLocked?: boolean;
  isInstructionsLocked?: boolean;
  isInstructionsComplete?: boolean;
  agent?: string;
  domainFilter?: string[];
};

export type UploadedTemplate = {
  id: string;
  file?: FileData;
  displayName: string;
  description: string;
  examplePopulatedFileId?: string;
  exampleTemplatedFileId?: string;
  supportsMulticlient: boolean;
  templateType: TemplateType;
  isActive: boolean;
  sections: TemplateSection[];
  createdAt?: string;
  markdownImageCollectionId: string | null;
  meetingNoteTense?: MeetingNoteTense;
};

export type TemplateWithoutSections = Omit<UploadedTemplate, 'sections'>;

export default function useTemplates({
  companyId,
  templateType,
}: {
  companyId?: string;
  templateType?: TemplateType;
}) {
  const isAdminEndpoint = !!companyId;

  const filters: CrudFilter[] = templateType
    ? [
        {
          field: 'templateType',
          operator: 'eq',
          value: templateType,
        },
      ]
    : [];

  const resource = isAdminEndpoint
    ? `admin/companies/${companyId}/data-export-templates`
    : 'data-export-templates';

  const {
    data,
    isFetching: isLoading,
    isError,
  } = useList<TemplateWithoutSections>({
    resource,
    filters,
  });

  const invalidate = useInvalidate();

  return {
    files: data?.data || [],
    isLoading,
    isError,
    refetch: () =>
      invalidate({
        resource,
        invalidates: ['list'],
      }),
  };
}
